import { isFeatureEnabled } from 'owa-feature-flags';
import { isSuccessStatusCode } from 'owa-http-status-codes';

export interface ProxyResponse {
    status: number;
    renderSuccess: boolean;
    statusText?: string;
    headers?: Headers;
}
export default function loadImageFromSrc(
    img: HTMLImageElement,
    src: string,
    delayRevoke: boolean
): Promise<ProxyResponse> {
    return isFeatureEnabled('rp-imageProxy-loadViaFetch')
        ? loadViaFetch(img, src, delayRevoke)
        : loadViaImageTag(img, src);
}

async function loadViaFetch(
    img: HTMLImageElement,
    src: string,
    delayRevoke: boolean
): Promise<ProxyResponse> {
    const resp = await fetch(src);

    return new Promise(async resolve => {
        if (isSuccessStatusCode(resp.status)) {
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            img.src = url;
            img.onload = e => {
                if (!delayRevoke) {
                    // once the image is loaded, we no longer need this URL
                    // revoking the URL allows the browser to GC the Blob
                    // https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL#Memory_management
                    URL.revokeObjectURL(url);
                }
                resolve({
                    status: resp.status,
                    renderSuccess: true,
                    statusText: resp.statusText,
                    headers: resp.headers,
                });
            };
            img.onerror = e => {
                resolve({
                    status: resp.status,
                    renderSuccess: false,
                    statusText: resp.statusText,
                    headers: resp.headers,
                });
            };
        } else {
            resolve({
                status: resp.status,
                renderSuccess: false,
                statusText: resp.statusText,
                headers: resp.headers,
            });
        }
    });
}

function loadViaImageTag(img: HTMLImageElement, src: string) {
    return new Promise<ProxyResponse>(resolve => {
        img.onload = e => {
            removeImageEventHandlers(img);
            resolve({ status: 200, renderSuccess: true });
        };

        img.onerror = e => {
            removeImageEventHandlers(img);
            resolve({ status: 500, renderSuccess: false });
        };

        img.src = src;
    });
}

function removeImageEventHandlers(img: HTMLImageElement) {
    img.onload = null;
    img.onerror = null;
}
